import React from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { FontSize } from "@themes/Fonts";
import { UserSelectNone } from "@themes/Rules";
import Styles from "@themes/Styles";
import { BrowserUtil } from "@util/index";

const Wrapper = styled.button<{
    variant: ButtonVariant;
    border: boolean;
    disabled: boolean;
}>`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    height: auto;
    margin: 0 4px;
    padding: 0 4px;

    // For colored buttons, opacity should be much lower (.25) vs a typical button with
    // background that matches the app's primary background (effectively transparent) should
    // have higher opacity if disabled to still be visible.
    opacity: ${(props) => (props.disabled && props.variant !== "default" ? 0.25 : props.disabled ? 0.7 : 1.0)};

    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

    color: ${(props) => (props.variant === "default" ? props.theme.textColorPrimary : "white")};

    background-color: ${(props) =>
        props.variant === "default" ? props.theme.backgroundPrimary : getColorForVariant(props.variant)};
    &:hover {
        background-color: ${(props) =>
            props.variant === "default" ? props.theme.hoverPrimary : getColorForVariant(props.variant)};
    }

    border: ${(props) => (props.border ? `1px solid ${props.theme.borderPrimary}` : "none")};
    border-radius: ${Styles.borderRadiusSharp};

    * {
        ${UserSelectNone};
    }
`;

const ButtonLabel = styled.span<{ onlyText: boolean }>`
    // Add extra space for text-only buttons
    margin: ${(props) => (props.onlyText ? "4px" : "0 4px")};
    font-size: ${(props) => (props.onlyText ? FontSize.normal : FontSize.mini)};
`;

const StyledIcon = styled.span`
    svg {
        fill: ${(props) => props.theme.iconColor};
    }
`;

type ButtonAction = "click" | "link";
type ButtonVariant = "default" | "blue" | "red" | "green";
interface StackedButtonProps {
    readonly label?: string;
    readonly action: ButtonAction;
    readonly variant?: ButtonVariant;
    readonly disabled?: boolean;
    readonly disabledTooltipText?: string;
    readonly icon?: React.ReactNode;
    readonly border?: boolean;
    readonly link?: string;
    readonly handleClick?: () => void;
    readonly className?: string;
}
export default function StackedButton(props: StackedButtonProps) {
    const handleClick = () => {
        switch (props.action) {
            case "click": {
                if (!props.handleClick) {
                    throw new Error("Click handler not defined for StackedButton");
                }
                props.handleClick();
                break;
            }
            case "link": {
                if (!props.link) {
                    throw new Error("Link not defined for StackedButton");
                }
                BrowserUtil.openLinkNewTab(props.link);
                break;
            }
            default: {
                throw new Error("Handle click action type not defined for StackedButton");
            }
        }
    };

    return (
        <>
            <Wrapper
                className={props.className}
                onClick={handleClick}
                variant={props.variant || "default"}
                border={props.border || false}
                disabled={props.disabled || false}
                data-tip={props.disabledTooltipText}
                data-for={`stacked-button-tooltip-${props.disabledTooltipText}`}
            >
                <StyledIcon>{props.icon}</StyledIcon>
                <ButtonLabel onlyText={props.icon === undefined}>{props.label}</ButtonLabel>
            </Wrapper>
            {props.disabled && props.disabledTooltipText && (
                <ReactTooltip id={`stacked-button-tooltip-${props.disabledTooltipText}`} html={true} />
            )}
        </>
    );
}

const getColorForVariant = (variant: ButtonVariant) => {
    switch (variant) {
        case "blue": {
            return Styles.buttonFillBlue;
        }
        case "red": {
            return Styles.buttonFillRed;
        }
        case "green": {
            return Styles.buttonFillGreen;
        }
        case "default": {
            return undefined;
        }
    }
};
